import moment from "moment";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  stringFormatingToPascal,
  getBadgeText,
  getBadgeClassByPolicyStatus,
  getPolicyType,
  getName,
  stringFormattingToCurrency,
} from "js/formatting";
import { filterAgentsByType } from "components/AgentPortal/PolicyDetails/PolicyCards/AgentInfoCard/utils/filterAgentsByType";
import { getPolicyDetails } from "service/policy/getPolicyDetails";
import { getFinancials } from "service/policy/getFinancials";
import {
  getAllContingentBeneficiaryFromParties,
  getAllInsuredsFromParties,
  getAllAnnuitantsFromParties,
  getAllOwnersFromParties,
  getAllPrimaryBeneficiaryFromParties,
} from "js/utilities";
import { getCorrespondenceList } from "redux/slices/getCorrespondenceListSlice";
import config from "config/config";
import { useContext } from "react";
import { AgentData } from "context/Agent/DataProvider";

export const usePolicyMapper = (policyNumber) => {
  let statementList = [];
  let scheduledPaymentList = [];
  const { agentPolicies: policies } = useContext(AgentData);

  const dispatch = useDispatch();
  const [error, setError] = useState();
  const [selectedPolicy, setSelectedPolicy] = useState();
  const [selectedPolicyFinancials, setSelectedPolicyFinancials] = useState();
  const { user } = useSelector((state) => state.user);
  const [policy, setPolicy] = useState({});
  const [policyDetail, setPolicyDetail] = useState({});
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [categories, setCategories] = useState([
    { typeOfDocument: "ALL", description: "All messages in your inbox" },
  ]);
  const [scheduledPayments, setScheduledPayments] = useState([]);
  const [statements, setStatements] = useState([]);

  useEffect(() => {
    if (policyNumber) {
      getPolicyDetails(policyNumber)
        .then((response) => {
          if (response === "Error") {
            setError("Server was unable to connect, please try again later.");
          } else {
            setSelectedPolicy(response);
            getFinancials(response)
              .then((response) => {
                if (response === "Error") {
                  setError(
                    "Server was unable to connect, please try again later."
                  );
                } else setSelectedPolicyFinancials(response);
              })
              .catch((error) => {
                console.error(
                  "Server refused to connect, please try again later.",
                  error
                );
                setError(
                  "Server was unable to connect, please try again later."
                );
              });
          }
        })
        .catch((error) => {
          console.error(
            "Server refused to connect, please try again later.",
            error
          );
          setError("Server was unable to connect, please try again later.");
        });
    }
  }, [policyNumber]);

  useEffect(() => {
    if (selectedPolicy) {
      let parties = getAllOwnersFromParties(selectedPolicy?.parties);
      let party = parties?.find(
        (x) => x.person?.taxId === user?.identityNumber
      );
      let insuredParty = getAllInsuredsFromParties(selectedPolicy?.parties);
      let primaryBeneficiaries = getAllPrimaryBeneficiaryFromParties(
        selectedPolicy?.parties
      );
      let contingents = getAllContingentBeneficiaryFromParties(
        selectedPolicy?.parties
      );
      // Dev Note: BELOW NEEDED FOR WHEN ENABLE_ALL_AGENTS_IN_POLICY_DETAILS_ON_AGENT_PORTAL === FALSE
      let agent = selectedPolicy?.agents?.find(
        (x) => x.agentType?.toLowerCase() === "servicing agent"
      );
      // Dev Note: BELOW NEEDED FOR WHEN ENABLE_ALL_AGENTS_IN_POLICY_DETAILS_ON_AGENT_PORTAL === TRUE
      let filteredAgents = filterAgentsByType(selectedPolicy?.agents);
      let contingentNames = [];
      if (contingents?.length > 0) {
        contingents.map((c) => {
          contingentNames.push(getName(c.person));
        });
      }

      setPolicy({
        ...selectedPolicy,
        banner: getBadgeClassByPolicyStatus(selectedPolicy),
        badgeStatus: getBadgeClassByPolicyStatus(selectedPolicy),
        badgeSrText: getBadgeText(selectedPolicy),
        badgeText: getBadgeText(selectedPolicy),
        currentDeathBenefitOption:
          selectedPolicy?.deathBenefit?.currentDeathBenefitOption,
        financials: selectedPolicyFinancials,
        policyNumber: selectedPolicy?.contractID,
        policyTitle:
          party?.person?.firstName && party?.person?.lastName
            ? `${
                selectedPolicy ? `${getPolicyType(selectedPolicy)} - ` : ""
              } ${getName(party?.person)}`
            : "",
        ownerTaxId: party?.person?.taxId,
        coverage: selectedPolicy?.cashValue?.currentFaceAmount,
        productName: selectedPolicy?.product?.description,
        value:
          selectedPolicy?.cashValue?.returnofpremiumbenefitAmount ?? "0.00",
        startDate: selectedPolicy.effectiveDate
          ? new Date(selectedPolicy.effectiveDate)?.toLocaleDateString()
          : "",
        endDate: selectedPolicy.maturityDate
          ? new Date(selectedPolicy.maturityDate).toLocaleDateString()
          : "",
        cta: "payment",
        faceAmount: selectedPolicy?.cashValue?.currentFaceAmount,
        deathBenefit: selectedPolicy?.cashValue?.deathBenefit,
        accountValue: selectedPolicy?.cashValue?.accountValue,
        surrenderCharge: selectedPolicy?.cashValue?.surrenderCharge,
        surrenderValue: selectedPolicy?.cashValue?.estimatedSurrenderValue,
        billingMethod: selectedPolicy?.billingType,
        draftDay: selectedPolicy?.bankInfo?.draftDay,
        mode: stringFormatingToPascal(selectedPolicy?.billingMode),
        modalPremium: selectedPolicy?.modalPremiumAmountTotal,
        paymentAmount: selectedPolicy?.modalPremiumAmountTotal,
        annualAmount:
          selectedPolicy?.modalPremiumAmountTotal &&
          selectedPolicy?.modalPremiumAmountTotal !== 0
            ? selectedPolicy?.modalPremiumAmountTotal * 6
            : 0,
        paymentMethod: selectedPolicy?.billingType,
        dueDate: selectedPolicy?.premiumDueDate,
        policyType: selectedPolicy ? `${getPolicyType(selectedPolicy)} - ` : "",
        policyTypeCode: selectedPolicy?.policyType,
        product: {
          type: selectedPolicy?.product?.type,
          line: selectedPolicy?.product?.line,
          description: selectedPolicy?.product?.description,
          marketingName: selectedPolicy?.product?.marketingName,
        },
      });

      setPolicyDetail({
        ...selectedPolicy,
        financials: selectedPolicyFinancials,
        pageTitle: `${
          selectedPolicy ? `${getPolicyType(selectedPolicy)} - ` : ""
        } ${getName(party?.person)}`,
        policy_detail: {
          ...selectedPolicy,
          status:
            selectedPolicy?.paymentStatus?.toLowerCase() === "approved"
              ? "good"
              : selectedPolicy?.paymentStatus?.toLowerCase() === "declined"
              ? "problem"
              : "risk",
          statusText:
            selectedPolicy?.paymentStatus?.toLowerCase() === "approved"
              ? "Policy is good"
              : selectedPolicy?.paymentStatus?.toLowerCase() === "declined"
              ? "Policy has problem"
              : "Policy is risk",
          policyNumber: selectedPolicy?.contractID,
          coverage: selectedPolicy?.cashValue?.currentFaceAmount,
          draftDay: selectedPolicy?.bankInfo?.draftDay,
          productName: selectedPolicy?.product?.description,
          productType: selectedPolicy?.product?.type,
          name: getPolicyType(selectedPolicy),
          startDate: selectedPolicy?.effectiveDate
            ? new Date(selectedPolicy?.effectiveDate)?.toLocaleDateString()
            : "",
          endDate: selectedPolicy?.maturityDate
            ? new Date(selectedPolicy?.maturityDate).toLocaleDateString()
            : "",
          title: "Copy",
          additionalBenefits:
            selectedPolicy?.supplementaryBenefitAndCharges?.filter(
              (x) => x.type !== "" && x.type !== "009"
            ),
        },
        people: {
          owner: `${getName(party?.person)}`,
          insured: `${getName(insuredParty?.person)}`,
          dob: `${
            insuredParty?.person?.dateofBirth
              ? insuredParty?.person?.dateofBirth
              : ""
          }`,
          issueAge: selectedPolicy.insuredIssueAge,
          address: {
            streetNumber: `${
              party?.address?.addressLine2 ? party?.address?.addressLine2 : ""
            }`,
            streetName: `${
              party?.address?.addressLine1 ? party?.address?.addressLine1 : ""
            }`,
            city: `${party?.address?.city ? party?.address?.city : ""}`,
            state: `${party?.address?.state ? party?.address?.state : ""}`,
            zipCode: `${
              party?.address?.postalCode
                ? party?.address?.postalCode?.substr(0, 5)
                : ""
            }`,
          },
          phone: [
            {
              number: `${
                party?.phone?.phoneNumber ? party?.phone?.phoneNumber : ""
              }`,
              type: `${party?.phoneNumber?.type ? party?.phone?.type : ""}`,
            },
          ],
          email: `${party?.email?.address ? party?.email?.address : ""}`,
          notification: ["Email"],
        },
        multipleOwner: parties,
        multipleInsured: getAllInsuredsFromParties(selectedPolicy?.parties),
        multipleAnnuitants: getAllAnnuitantsFromParties(
          selectedPolicy?.parties
        ),
        beneficiaries: {
          primary: primaryBeneficiaries,
          contingent: contingents,
        },
        paymentSummary: {
          ...selectedPolicy,
          status:
            selectedPolicy?.paymentStatus?.toLowerCase() === "approved"
              ? "good"
              : selectedPolicy?.paymentStatus?.toLowerCase() === "declined"
              ? "problem"
              : "risk",
          dueDate: selectedPolicy?.premiumDueDate,
          frequency: selectedPolicy?.billingMode,
          amount: selectedPolicy?.modalPremiumAmountTotal,
          method: selectedPolicy?.billingType,
        },
        // Dev Note: BELOW NEEDED FOR WHEN ENABLE_ALL_AGENTS_IN_POLICY_DETAILS_ON_AGENT_PORTAL === TRUE
        agents: filteredAgents,
        // Dev Note: BELOW NEEDED FOR WHEN ENABLE_ALL_AGENTS_IN_POLICY_DETAILS_ON_AGENT_PORTAL === FALSE
        agent: {
          name: getName(agent?.parties[0].person),
          address: {
            streetNumber: agent?.parties[0].address?.addressLine2,
            streetName: agent?.parties[0].address?.addressLine1,
            city: agent?.parties[0].address?.city,
            state: agent?.parties[0].address?.state,
            zipCode: agent?.parties[0].address?.postalCode?.substr(0, 5),
          },
          phone: agent?.parties[0]?.phone?.phoneNumber,
          email: agent?.parties[0]?.email?.address,
        },
        deposits: selectedPolicy?.cashValue?.grossTransactions?.deposits,
        withdrawals: selectedPolicy?.cashValue?.grossTransactions?.withdrawals,
        policyTypeCode: selectedPolicy?.policyType,
        product: {
          type: selectedPolicy?.product?.type,
          line: selectedPolicy?.product?.line,
          description: selectedPolicy?.product?.description,
          marketingName: selectedPolicy?.product?.marketingName,
        },
      });
    }
  }, [selectedPolicy, policyNumber, user]);

  useEffect(() => {
    if (policyNumber) {
      dispatch(
        getCorrespondenceList({
          policyId: [policyNumber],
          agentPolicies: policies,
          agentId: "",
          beginDate: moment()
            .subtract(parseInt(config.ANNUAL_STATEMENT_DURATION), "year")
            .format("MM/DD/YYYY"),
          endDate: moment().format("MM/DD/YYYY"),
        })
      )
        .then((response) => {
          if (response?.meta?.requestStatus === "fulfilled") {
            let alerts = response.payload.allAlerts.filter(
              (x) =>
                x.document.typeOfDocument.toUpperCase() === "PREMNOT" ||
                x.document.typeOfDocument.toUpperCase() === "PREMNOT_OW1" ||
                x.document.typeOfDocument.toUpperCase() === "ANNSTMT" ||
                x.document.typeOfDocument.toUpperCase() === "TAX" ||
                x.document.typeOfDocument.toUpperCase() === "DIRECTBILL" ||
                x.document.typeOfDocument.toUpperCase() === "REMINDERNOTICE" ||
                x.document.typeOfDocument.toUpperCase() === "LAPSENOTICE"
            );
            // Sorted by Newest to Oldest
            setFilteredDocuments(
              alerts?.sort(function (a, b) {
                if (
                  new Date(a.document.dateOfDocument) <
                  new Date(b.document.dateOfDocument)
                ) {
                  return 1;
                }
                if (
                  new Date(a.document.dateOfDocument) >
                  new Date(b.document.dateOfDocument)
                ) {
                  return -1;
                }
                return 0;
              })
            );
            setAlerts(response.payload.allAlerts);
            setCategories((currentCategories) => [
              ...currentCategories,
              ...response.payload.allCategories,
            ]);
          }
        })
        .catch((error) => {
          console.log("Error", error?.message);
        });
    }
  }, [policyNumber]);

  useEffect(() => {
    let p = policies.find((x) => x.contractID === policyNumber);
    let party = p?.parties?.find(
      (x) => x.person?.type?.toLowerCase() === "primary owner"
    );
    if (
      p?.draftDate &&
      p?.draftDate !== "" &&
      moment(p.draftDate).format("MM/DD/YYYY") > moment().format("MM/DD/YYYY")
    ) {
      scheduledPaymentList.push({
        oldPolicy: p?.oldPolicy,
        policyName: `${p ? `${getPolicyType(p)} - ` : ""} ${getName(
          party?.person
        )}`,
        policyNumber: policyNumber,
        policy_id: policyNumber,
        amount: stringFormattingToCurrency(p?.modalPremiumAmountTotal),
        method: p?.billingType,
        scheduledDate: moment(p.draftDate).format("MM/DD/YYYY"),
        postedDate: moment(p?.nextPaidDueDate).format("MM/DD/YYYY"),
      });
      setScheduledPayments(scheduledPaymentList);
    }
    if (filteredDocuments?.length > 0) {
      filteredDocuments?.map((document) => {
        if (
          (policy?.billingType?.toLowerCase() === "direct bill" ||
            policy?.billingType?.toLowerCase() === "notice billing") &&
          document?.policy?.contractID === policyNumber &&
          (document?.document?.typeOfDocument.toUpperCase() === "PREMNOT_OW1" ||
            document?.document?.typeOfDocument.toUpperCase() === "PREMNOT" ||
            document?.document?.typeOfDocument.toUpperCase() === "DIRECTBILL" ||
            document?.document?.typeOfDocument.toUpperCase() ===
              "REMINDERNOTICE" ||
            document?.document?.typeOfDocument.toUpperCase() === "LAPSENOTICE")
        ) {
          statementList.push({
            statementName: `${document?.document?.typeOfDocument} - Policy# ${p.oldPolicy}`,
            dateAdded: `${moment(document?.document?.dateOfDocument).format(
              "MM/DD/YYYY"
            )}`,
            type: `Policy-${document?.policy?.contractID}-${moment(
              document?.document?.dateOfDocument
            ).format("MM/DD/YYYY")} `,
            document: document,
          });
        }
        setStatements(statementList);
      });
    }
  }, [filteredDocuments]);

  useEffect(() => {
    if (selectedPolicyFinancials) {
      setPolicyDetail((prevDetail) => ({
        ...prevDetail,
        financials: selectedPolicyFinancials,
      }));
      setPolicy((prevPolicy) => ({
        ...prevPolicy,
        financials: selectedPolicyFinancials,
      }));
    }
  }, [selectedPolicyFinancials]);

  return {
    policy: policy,
    policyDetail: policyDetail,
    filteredDocuments: filteredDocuments,
    alerts: alerts,
    categories: categories,
    scheduledPayments: scheduledPayments,
    statements: statements,
    error: error,
  };
};
